import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';

import { filter, map, switchMap, tap } from 'rxjs/operators';

import { AuditLogsActions } from 'src/app/features/audit-logs';
import { CheckInActions } from 'src/app/features/check-in/store';
import { NextStep } from 'src/app/features/check-in/models';
import { DeviceGroupActions, DeviceGroupSelectors } from 'src/app/features/device-group';
import { MsalAuthSelectors } from 'src/app/features/msal-auth/store/selectors';
import { MultiFactorActions } from 'src/app/features/multi-factor/store/actions';
import { PackageUsersSelectors } from 'src/app/features/package-users';
import { PackagesSelectors } from 'src/app/features/packages';
import { PinValidationActions } from 'src/app/features/pin-validation';
import { AudioVideoCheckSelectors } from 'src/app/features/av-check/store/selectors';
import { SignalRActions } from 'src/app/features/signal-r';

import { SessionHandoffActions } from '../actions';
import {
  getAssociatedUsers,
  selectIsVerifiedParticipant,
} from '../selectors/session-handoff.selectors';

@Injectable()
export class SessionHandoffEffects {
  activateSessionHandoff$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionHandoffActions.ActivateSessionHandoff),
      concatLatestFrom(() => [
        this.store.select(DeviceGroupSelectors.getUsersOnDevice),
        this.store.select(getAssociatedUsers),
      ]),
      switchMap(([_, usersOnDevice, associatedUsers]) => {
        const usersOnDeviceButNotAssociated = usersOnDevice.filter(
          (deviceUser) =>
            !associatedUsers.some(
              (userGuid) => userGuid.toLowerCase() === deviceUser.packageUserGuid.toLowerCase()
            )
        );
        return usersOnDeviceButNotAssociated.map((deviceUser) =>
          SessionHandoffActions.AddAssociatedUser({
            payload: { packageUserGuid: deviceUser.packageUserGuid },
          })
        );
      })
    )
  );

  // A borrower whose identity B2C already established has nothing left to confirm, so join them
  // as soon as the page opens rather than making them press Continue. joinSession$ re-checks that
  // the token belongs to this participant before it actually skips the challenge.
  // Multi-participant orders are excluded: each participant confirms individually there.
  autoJoinVerifiedParticipant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionHandoffActions.ActivateSessionHandoff),
      concatLatestFrom(() => [
        this.store.select(MsalAuthSelectors.getIsUserVerified),
        this.store.select(MsalAuthSelectors.getUserGuid),
        this.store.select(PackageUsersSelectors.selectHasMultipleNonSigningAgents),
      ]),
      filter(
        ([_, isUserVerified, authenticatedUserGuid, hasMultipleNonSigningAgents]) =>
          isUserVerified && !!authenticatedUserGuid && !hasMultipleNonSigningAgents
      ),
      map(([_, __, authenticatedUserGuid]) =>
        SessionHandoffActions.JoinSession({
          payload: { packageUserGuid: authenticatedUserGuid },
        })
      )
    )
  );

  joinSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionHandoffActions.JoinSession),
      concatLatestFrom((action) => [
        this.store.select(PackageUsersSelectors.getPackageUser(action.payload.packageUserGuid)),
        this.store.select(selectIsVerifiedParticipant(action.payload.packageUserGuid)),
      ]),
      switchMap(([action, participant, isVerifiedParticipant]) => {
        if (!participant || participant.isMissingVerificationType) {
          return [];
        }

        if (isVerifiedParticipant) {
          return [
            SessionHandoffActions.JoinSessionSuccess({
              // Rebuilt payload, so isWaitingRoom must be carried explicitly; the MFA and
              // PIN branches below forward action.payload and get it for free.
              payload: {
                packageUserGuid: participant.packageUserGuid,
                ...(action.payload.isWaitingRoom ? { isWaitingRoom: true } : {}),
              },
            }),
          ];
        }

        if (participant.isMfaRequired) {
          return [
            MultiFactorActions.StartMultiFactorChallengeFromModalComponent({
              payload: {
                packageUserGuid: participant.packageUserGuid,
                onSuccessAction: SessionHandoffActions.JoinSessionSuccess({
                  payload: action.payload,
                }),
              },
            }),
          ];
        }

        return [
          PinValidationActions.StartPinChallenge({
            payload: {
              participant: participant,
              onSuccessAction: SessionHandoffActions.JoinSessionSuccess({
                payload: action.payload,
              }),
            },
          }),
        ];
      })
    )
  );

  joinSessionSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionHandoffActions.JoinSessionSuccess),
      switchMap(({ payload }) => {
        const { packageUserGuid, isWaitingRoom } = payload;
        const joinPayload = { packageUserGuid };

        return [
          SessionHandoffActions.AddAssociatedUser({ payload: joinPayload }),
          SignalRActions.AttemptSignalRHubConnection({ payload: joinPayload }),
          AuditLogsActions.SaveReadyUpEvent({ payload: joinPayload }),
          CheckInActions.ActiveSessionCheckInCompletion({ payload: joinPayload }),
          CheckInActions.SetUserCheckInComplete({ payload: joinPayload }),
          DeviceGroupActions.SaveToDeviceGroup({ payload: joinPayload }),
          // Safe alongside SaveToDeviceGroup: the single-participant endpoint does not depend
          // on AssociatedParticipants state, so it cannot lose that race.
          ...(isWaitingRoom
            ? [CheckInActions.WaitingRoomViewedByParticipant({ payload: joinPayload })]
            : []),
        ];
      })
    )
  );

  continueToSession$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SessionHandoffActions.ContinueToSession),
        concatLatestFrom(() => [
          this.store.pipe(
            select(AudioVideoCheckSelectors.getDoesAvCheckNeedToBeCompletedInSession)
          ),
          this.store.pipe(select(PackagesSelectors.getProductRoute)),
        ]),
        tap(([_, needsAvCheck, productRoute]) => {
          if (needsAvCheck) {
            this.router.navigate([`/${productRoute}/av-check`], {
              queryParamsHandling: 'preserve',
            });
          } else {
            this.router.navigate([`/${productRoute}/waiting-room`], {
              queryParamsHandling: 'preserve',
            });
          }
        }),
        map(() => CheckInActions.SetNextStep({ payload: { nextStep: NextStep.WaitingRoom } }))
      )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly router: Router,
  ) {}
}
